/**
 * Ce que l'écran de note de crédit lit et écrit, en un seul endroit.
 *
 * ## Pourquoi une passerelle séparée
 *
 * `plan.ts` et `tariffs.ts` sont purs : ils décident, et se testent sans base.
 * Ce fichier est leur seul point de contact avec Supabase. Le découpage n'est
 * pas décoratif — il permet de tester les règles d'argent sans réseau, et il
 * rend visible tout ce qui touche la base pour cette fonctionnalité : quatre
 * fonctions, pas une de plus.
 *
 * ## L'émission passe par `supabase.rpc`, jamais par un `fetch`
 *
 * L'ancien écran appelait :
 *
 *     fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-consolidated-credit-note`, …)
 *
 * Compiler ce code sans `VITE_SUPABASE_URL` produit littéralement l'URL
 * `undefined/functions/v1/create-consolidated-credit-note` dans le bundle —
 * vérifié en construisant le dépôt sans fichier `.env`. Vite remplace la
 * variable à la compilation, et une chaîne assemblée à la main ne se plaint de
 * rien : la panne attend le clic de l'utilisateur, sur l'émission d'une note
 * de crédit.
 *
 * `supabase.rpc()` passe par le client déjà construit. S'il est mal configuré,
 * rien ne marche dès l'ouverture de l'application — ce qui se voit
 * immédiatement, au lieu de se découvrir sur un geste comptable.
 *
 * Treize autres appels du dépôt sont bâtis de la même façon. Ce n'est pas la
 * preuve qu'ils sont cassés en production, où la variable est probablement
 * fournie : c'est la preuve qu'aucun d'eux ne le dirait avant le clic.
 */

import { supabase } from '../supabase';
import { messageDeFonction, messageErreur } from '../payments/erreur';
import type {
  CreditNotePlan,
  InvoiceForCredit,
  RegistrationForCredit,
} from './plan';
import type { SessionTariffs } from './tariffs';

/** Une inscription de la facture, avec ce qu'il faut pour proposer un tarif. */
export interface RegistrationDetail extends RegistrationForCredit {
  kidName: string;
  stageTitle: string;
  centerName: string;
  startDate: string;
  endDate: string;
  tariffs: SessionTariffs;
}

export interface InvoiceDetail {
  invoice: InvoiceForCredit;
  familyName: string;
  familyEmail: string;
  pdfUrl: string | null;
  registrations: RegistrationDetail[];
  /**
   * Les inscriptions liées par `registrations.invoice_id` mais absentes de
   * `invoices.registration_ids`.
   *
   * Les deux liens ne sont pas d'accord sur 46 liens en production. Les compter
   * séparément permet de le dire à l'écran au lieu de perdre l'inscription en
   * silence — ce que faisait l'ancien écran, qui ne lisait que le tableau.
   */
  orphanCount: number;
}

/** Une ligne d'inscription telle que la requete ci-dessous la rend. */
interface LigneInscription {
  id: string;
  amount_paid: number | string | null;
  price_type: string | null;
  cancellation_status: string | null;
  kid: { prenom: string | null; nom: string | null } | null;
  session: {
    start_date: string | null;
    end_date: string | null;
    prix_normal: number | string | null;
    prix_reduit: number | string | null;
    prix_local: number | string | null;
    prix_local_reduit: number | string | null;
    stage: { title: string | null } | null;
    center: { name: string | null } | null;
  } | null;
}

function euros(v: unknown): number {
  const n = typeof v === 'number' ? v : Number(v ?? 0);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

function jour(d: string | null): string {
  if (!d) return '';
  const t = new Date(d);
  return Number.isNaN(t.getTime()) ? '' : t.toLocaleDateString('fr-BE');
}

/**
 * Charge tout ce que la modale doit savoir sur une facture.
 *
 * Les soldes viennent de `v_invoice_balances`, pas de `invoices` : c'est la vue
 * qui sait déduire les notes de crédit déjà émises et sommer les paiements
 * affectés. Lire `invoices.amount` seul, comme le faisait l'ancien écran,
 * laissait le plafond ignorer les notes antérieures.
 */
export async function loadInvoiceDetail(invoiceId: string): Promise<InvoiceDetail> {
  const { data: solde, error: eSolde } = await supabase
    .from('v_invoice_balances')
    .select(
      'invoice_id, invoice_number, status, amount_cents, credit_notes_cents, allocated_cents, pdf_url, user_id',
    )
    .eq('invoice_id', invoiceId)
    .maybeSingle();

  if (eSolde) throw new Error(messageErreur(eSolde, 'Facture illisible'));
  if (!solde) throw new Error('Facture introuvable');

  const { data: famille } = await supabase
    .from('users')
    .select('prenom, nom, email')
    .eq('id', solde.user_id)
    .maybeSingle();

  // Le tableau porté par la facture, et le lien inverse porté par les
  // inscriptions : les DEUX sont interrogés, parce qu'ils divergent.
  const { data: facture } = await supabase
    .from('invoices')
    .select('registration_ids')
    .eq('id', invoiceId)
    .maybeSingle();

  const parTableau: string[] = facture?.registration_ids ?? [];

  const champs = `
    id, kid_id, activity_id, amount_paid, price_type, cancellation_status,
    payment_status,
    kid:kids(prenom, nom),
    session:activity_id(
      start_date, end_date,
      prix_normal, prix_reduit, prix_local, prix_local_reduit,
      stage:stage_id(title),
      center:center_id(name)
    )
  `;

  /*
    Les lignes reviennent sans type genere : la requete imbrique `kids` et
    `sessions`, et les types Supabase du depot ne couvrent pas ces jointures.
    Une forme minimale declaree ici vaut mieux qu'un `any` — elle dit ce que le
    code lit vraiment, et le compilateur verifie au moins ces champs.
  */
  const [{ data: parId }, { data: parNumero }] = await Promise.all([
    parTableau.length > 0
      ? supabase.from('registrations').select(champs).in('id', parTableau)
      : Promise.resolve({ data: [] as LigneInscription[] }),
    supabase.from('registrations').select(champs).eq('invoice_id', solde.invoice_number),
  ]);

  const brutes = new Map<string, LigneInscription>();
  for (const r of parId ?? []) brutes.set(r.id, r);
  let orphelines = 0;
  for (const r of parNumero ?? []) {
    if (!brutes.has(r.id)) {
      orphelines += 1;
      brutes.set(r.id, r);
    }
  }

  // Ce qui a déjà été crédité inscription par inscription. Sans cette lecture,
  // une deuxième note de crédit recréditerait un stage déjà remboursé.
  const ids = [...brutes.keys()];
  const dejaCredite = new Map<string, number>();
  if (ids.length > 0) {
    const { data: lignes } = await supabase
      .from('credit_note_registrations')
      .select('registration_id, amount_cents')
      .in('registration_id', ids);
    for (const l of lignes ?? []) {
      dejaCredite.set(l.registration_id, (dejaCredite.get(l.registration_id) ?? 0) + (l.amount_cents ?? 0));
    }
  }

  const registrations: RegistrationDetail[] = [...brutes.values()].map((r) => {
    const s = r.session ?? {};
    const enfant = [r.kid?.prenom, r.kid?.nom].filter(Boolean).join(' ') || 'Enfant inconnu';
    const stage = s.stage?.title ?? 'Stage inconnu';
    return {
      id: r.id,
      label: `${stage} — ${enfant}, ${jour(s.start_date)}`,
      priceType: r.price_type ?? '',
      amountCents: euros(r.amount_paid),
      cancellationStatus: r.cancellation_status ?? 'none',
      alreadyCreditedCents: dejaCredite.get(r.id) ?? 0,
      kidName: enfant,
      stageTitle: stage,
      centerName: s.center?.name ?? '',
      startDate: jour(s.start_date),
      endDate: jour(s.end_date),
      tariffs: {
        normal: s.prix_normal == null ? null : euros(s.prix_normal),
        reduced: s.prix_reduit == null ? null : euros(s.prix_reduit),
        local: s.prix_local == null ? null : euros(s.prix_local),
        local_reduced: s.prix_local_reduit == null ? null : euros(s.prix_local_reduit),
      },
    };
  });

  return {
    invoice: {
      id: solde.invoice_id,
      invoiceNumber: solde.invoice_number,
      amountCents: solde.amount_cents ?? 0,
      creditNotesCents: solde.credit_notes_cents ?? 0,
      allocatedCents: Number(solde.allocated_cents ?? 0),
      status: solde.status ?? 'pending',
    },
    familyName: [famille?.prenom, famille?.nom].filter(Boolean).join(' ') || '—',
    familyEmail: famille?.email ?? '',
    pdfUrl: solde.pdf_url ?? null,
    registrations,
    orphanCount: orphelines,
  };
}

export interface EmissionResult {
  creditNoteId: string;
  creditNoteNumber: string;
  amountCents: number;
  registrationsCancelled: number;
  registrationsRequalified: number;
  provisionCents: number;
  /**
   * Ce que la facture ne pouvait plus garder, que l'écran l'ait demandé ou non.
   *
   * La base replafonne la provision par cet excès : `p_provision_cents` ne peut
   * qu'en demander moins. Le renvoyer permet de signaler un excès resté sur
   * place, plutôt que de faire confiance à l'aperçu.
   */
  excessCents: number;
  /** L'adresse du PDF produit après l'émission, ou `null`. */
  pdfUrl: string | null;
  /** Pourquoi le PDF n'a pas été produit. La note existe et compte quand même. */
  pdfError: string | null;
}

/**
 * Émet la note de crédit décrite par le plan.
 *
 * Le plan est traduit en arguments, pas envoyé tel quel : la fonction serveur
 * refuse `price_type_before` et `amount_before_cents`, qu'elle relit
 * elle-même sur l'inscription. Un écran ne doit pas pouvoir falsifier la trace
 * de ce qui avait été facturé.
 */
export async function emitCreditNote(
  invoiceId: string,
  plan: CreditNotePlan,
  adminNotes: string,
): Promise<EmissionResult> {
  const { data, error } = await supabase.rpc('create_credit_note', {
    p_invoice_id: invoiceId,
    p_amount_cents: plan.amountCents,
    p_reason: plan.reason,
    p_notes: adminNotes.trim() === '' ? null : adminNotes.trim(),
    p_lines: plan.lines.map((l) => ({
      registration_id: l.registrationId,
      amount_cents: l.amountCents,
      price_type_after: l.priceTypeAfter,
      amount_after_cents: l.amountAfterCents,
      cancel: l.registrationCancelled,
    })),
    p_provision_cents: plan.provisionCents,
  });

  if (error) throw new Error(messageErreur(error, 'Émission impossible'));

  const r = (data ?? {}) as Record<string, unknown>;
  const creditNoteId = String(r.credit_note_id ?? '');

  /*
    Le PDF est produit APRÈS l'émission, jamais dedans.

    La fonction PostgreSQL s'exécute dans une transaction : un PDF généré
    pendant cette transaction survivrait à son annulation, et laisserait un
    document sans note de crédit derrière lui. C'est aussi ce qui rend sans
    danger la réutilisation d'un numéro libéré par un ROLLBACK.

    Et si la génération échoue, l'émission reste acquise : la note existe, elle
    compte dans les soldes, et le PDF se régénère. `invoke` passe par le client
    déjà configuré — pas par une URL assemblée à la main.
  */
  let pdfUrl: string | null = null;
  let pdfError: string | null = null;
  if (creditNoteId !== '') {
    try {
      const { data: pdf, error: ePdf } = await supabase.functions.invoke(
        'generate-credit-note-pdf',
        { body: { credit_note_id: creditNoteId } },
      );
      if (ePdf) throw ePdf;
      const p = (pdf ?? {}) as Record<string, unknown>;
      if (typeof p.pdf_url === 'string') pdfUrl = p.pdf_url;
      else if (typeof p.error === 'string') pdfError = p.error;
    } catch (e) {
      pdfError = messageErreur(e, 'Génération du PDF impossible');
    }
  }

  return {
    creditNoteId,
    creditNoteNumber: String(r.credit_note_number ?? ''),
    amountCents: Number(r.amount_cents ?? 0),
    registrationsCancelled: Number(r.registrations_cancelled ?? 0),
    registrationsRequalified: Number(r.registrations_requalified ?? 0),
    provisionCents: Number(r.provision_cents ?? 0),
    excessCents: Number(r.excess_cents ?? 0),
    pdfUrl,
    pdfError,
  };
}

/**
 * La longueur maximale du message à la famille. Alignée sur `MESSAGE_MAX` de
 * `supabase/functions/send-credit-note-email/corps.ts`, qui refuse au-delà
 * plutôt que de couper : l'écran l'empêche avant.
 */
export const MESSAGE_FAMILLE_MAX = 2000;

/** Ce qu'a donné l'envoi d'une note de crédit par courriel. */
export interface EnvoiDeNote {
  /** L'adresse où le courriel est parti, ou `null` s'il n'est pas parti. */
  envoyeA: string | null;
  /** Pourquoi il n'est pas parti, dans les mots de la fonction. `null` sinon. */
  erreur: string | null;
}

/**
 * Envoyer une note de crédit à sa famille, avec son PDF et un message
 * facultatif.
 *
 * Ne lève jamais. Quand on l'appelle, la note existe déjà et compte dans les
 * soldes : un envoi raté ne doit pas se lire comme une émission ratée, sinon
 * on réémet la note. L'appelant dit ce qui s'est passé.
 *
 * `messageDeFonction` et non `messageErreur` : la fonction met sa raison dans
 * le corps de la réponse (« le PDF n'a pas pu être produit : rien n'a été
 * envoyé »), et `invoke` ne la remonte pas d'elle-même.
 */
export async function envoyerNoteDeCredit(
  creditNoteId: string,
  message: string,
): Promise<EnvoiDeNote> {
  try {
    const { data, error } = await supabase.functions.invoke('send-credit-note-email', {
      body: { credit_note_id: creditNoteId, message: message.trim() === '' ? null : message },
    });
    if (error) {
      return { envoyeA: null, erreur: await messageDeFonction(error, 'Envoi du courriel impossible') };
    }
    const r = (data ?? {}) as { sent_to?: unknown; error?: unknown };
    if (typeof r.sent_to === 'string') return { envoyeA: r.sent_to, erreur: null };
    return {
      envoyeA: null,
      erreur: typeof r.error === 'string' ? r.error : 'Réponse inattendue du service de courriel',
    };
  } catch (e) {
    return { envoyeA: null, erreur: messageErreur(e, 'Envoi du courriel impossible') };
  }
}
